import { NgClass, NgStyle } from '@angular/common';
import {
  AfterViewInit,
  Component,
  OnInit,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import {
  NavigationEnd,
  NavigationStart,
  Router,
  RouterModule,
} from '@angular/router';
import { filter } from 'rxjs';
import { HSStaticMethods } from 'preline/non-auto';
import { cleanupHsSelectOnNavigation } from 'ui/advanced-select/hs-select-cleanup';
import { Header } from 'components/header/header';
import { Navbar } from 'components/navbar/navbar';
import { Sidebar } from 'components/sidebar/sidebar';
import { getNavbarNavForRole } from 'core/constants/nav-navbar-by-role';
import { getRoleThemeCssVars } from 'core/constants/role-theme';
import { ROLE_LABELS } from 'core/models/role.model';
import { AuthSessionService } from 'core/services/auth-session.service';
import { ClassService } from 'core/services/class.service';
import { PlatformSettingsService } from 'core/services/platform-settings.service';
import { SessionIdleService } from 'core/services/session-idle.service';
import { getOneEducaTrialBannerFromJwt } from 'core/utils/jwt-claims.util';
import { getPanelTitle } from 'core/constants/panel-title';
@Component({
  imports: [RouterModule, NgClass, NgStyle, Header, Sidebar, Navbar],
  selector: 'app-root',
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App implements OnInit, AfterViewInit {
  private readonly router = inject(Router);
  private readonly auth = inject(AuthSessionService);
  private readonly classService = inject(ClassService);
  private readonly platformSettings = inject(PlatformSettingsService);

  protected readonly currentUrl = signal(
    typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.search}`
      : this.router.url,
  );
  protected readonly showAdminShell = computed(() => {
    if (!this.auth.isAuthenticated()) {
      return false;
    }

    const path = this.currentUrl().split('?')[0] || '/';
    return (
      path !== '/login' &&
      path !== '/auth/callback' &&
      path !== '/session-expired' &&
      path !== '/maintenance' &&
      path !== '/validar' &&
      !path.startsWith('/validar/')
    );
  });
  protected readonly navigationLayout = computed(
    () => this.platformSettings.current().navigationLayout,
  );
  protected readonly isNavbarLayout = computed(
    () => this.navigationLayout() === 'navbar',
  );
  protected readonly isSidebarOpen = signal(this.shouldSidebarStartOpen());

  protected readonly currentRole = computed(() => this.auth.role());
  protected readonly themeCssVars = computed(() =>
    getRoleThemeCssVars(this.currentRole()),
  );
  protected readonly navbarItems = computed(() =>
    getNavbarNavForRole(
      this.currentRole(),
      this.platformSettings.current().groupNavMode,
    ),
  );
  protected readonly userName = computed(
    () => this.auth.user()?.name ?? 'Usuário',
  );
  protected readonly userRoleLabel = computed(() => {
    const role = this.currentRole();
    return role ? ROLE_LABELS[role] : '';
  });

  protected readonly headerTitle = computed(() =>
    getPanelTitle(this.currentRole()),
  );

  protected readonly loginCandidates = computed(() =>
    this.auth.canSwitchDemoUser() ? this.auth.loginCandidates() : [],
  );
  protected readonly currentUserId = computed(() => this.auth.user()?.id ?? '');

  protected readonly trialBannerMessage = computed(() => {
    const session = this.auth.current();
    if (!session || session.provider !== 'oneauth' || !session.token) {
      return null;
    }

    return getOneEducaTrialBannerFromJwt(session.token)?.message ?? null;
  });

  /** Altura do banner de trial (h-10). */
  protected readonly shellTopOffset = computed(() =>
    this.trialBannerMessage() ? '2.5rem' : '0px',
  );

  protected readonly sidebarToggleTop = computed(() =>
    this.trialBannerMessage() ? 'calc(2.5rem + 25px)' : '25px',
  );

  protected readonly navbarTop = computed(() =>
    this.trialBannerMessage() ? 'calc(2.5rem + 50px)' : '50px',
  );

  protected readonly mainContentTop = computed(() => {
    const chrome = this.isNavbarLayout() ? '6.125rem' : '50px';
    return this.trialBannerMessage() ? `calc(2.5rem + ${chrome})` : chrome;
  });

  constructor() {
    // Mantém o watcher de inatividade ativo no shell.
    inject(SessionIdleService);

    effect(() => {
      const vars = this.themeCssVars();
      if (typeof document === 'undefined') {
        return;
      }
      const root = document.documentElement;
      for (const [key, value] of Object.entries(vars)) {
        root.style.setProperty(key, value);
      }
    });
  }

  ngOnInit(): void {
    if (this.auth.isAuthenticated() && !this.auth.useMockAuth) {
      this.auth.revalidateFromApi().subscribe();
    }

    this.router.events
      .pipe(
        filter(
          (event) =>
            event instanceof NavigationStart || event instanceof NavigationEnd,
        ),
      )
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          cleanupHsSelectOnNavigation();
          return;
        }
        this.currentUrl.set(event.urlAfterRedirects);
        setTimeout(() => HSStaticMethods.autoInit(), 100);
      });
  }

  ngAfterViewInit(): void {
    HSStaticMethods.autoInit();
  }

  protected onToggleSidebar(): void {
    this.isSidebarOpen.update((open) => !open);
  }

  protected onCloseSidebar(): void {
    this.isSidebarOpen.set(false);
  }

  protected onLogout(): void {
    this.auth.logout();
    if (this.auth.useMockAuth) {
      void this.router.navigate(['/login']);
    }
  }

  protected onSwitchUser(userId: string): void {
    if (!this.auth.canSwitchDemoUser()) {
      return;
    }
    if (!userId || userId === this.currentUserId()) {
      return;
    }
    this.auth.switchUser(userId);
    this.classService.invalidateListCache();
    void this.router.navigate(['/']);
  }

  private shouldSidebarStartOpen(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.matchMedia('(min-width: 1024px)').matches;
  }
}
